import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataTableElement from "../../../components/elements/DataTableElement";
import FiltersPagination from "../../../components/elements/FiltersPagination";
import { currencyUSD } from "../../../helpers/forms/formater";
import {
    invoiceClientLoads,
    invoiceClients,
    invoicesResource,
} from "../../../helpers/configRoutes/backend_helper";

const statusClass = (status) =>
    status === "Paid"
        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
        : "bg-amber-50 text-amber-700 border-amber-200";

const AccountingSettlements = () => {
    const navigate = useNavigate();
    const [filters, setFilters] = useState({ search: "", page: 1, status: "" });
    const [meta, setMeta] = useState({ total: 0, current_page: 0 });
    const [tableLoad, setTableLoad] = useState({ show: false, textLoad: "Loading invoices..." });
    const [invoices, setInvoices] = useState([]);
    const [pendingClients, setPendingClients] = useState([]);
    const [processingClientId, setProcessingClientId] = useState(null);
    const [processingInvoiceId, setProcessingInvoiceId] = useState(null);

    const loadInvoices = useCallback(async () => {
        setTableLoad({ show: true, textLoad: "Loading invoices..." });
        const response = await invoicesResource("get", { params: filters });
        setInvoices(response?.invoices || []);
        setMeta(response?.meta || { total: 0, current_page: 0 });
        setTableLoad((prev) => ({ ...prev, show: false }));
    }, [filters]);

    const loadPendingClients = useCallback(async () => {
        const response = await invoiceClients();
        setPendingClients(response?.clients || []);
    }, []);

    useEffect(() => {
        loadInvoices();
    }, [loadInvoices]);

    useEffect(() => {
        loadPendingClients();
    }, [loadPendingClients]);

    const refreshAll = async () => {
        await Promise.all([loadInvoices(), loadPendingClients()]);
    };

    const createInvoiceForClient = async (clientId) => {
        setProcessingClientId(clientId);
        const loadsResponse = await invoiceClientLoads(clientId);
        const loadIds = (loadsResponse?.loads || []).map((load) => load.id);

        if (loadIds.length) {
            const response = await invoicesResource("post", {
                client_id: clientId,
                load_ids: loadIds,
                status: "Unpaid",
            });

            if (response?.status) {
                await refreshAll();
            }
        }

        setProcessingClientId(null);
    };

    const updateInvoiceStatus = useCallback(async (invoice, nextStatus) => {
        if (!invoice?.id || invoice.status === nextStatus) return;

        setProcessingInvoiceId(invoice.id);
        const response = await invoicesResource("put", { status: nextStatus }, invoice.id);

        if (response?.status) {
            await loadInvoices();
        }

        setProcessingInvoiceId(null);
    }, [loadInvoices]);

    const columns = useMemo(() => [
        {
            id: "invoice_number",
            name: "INVOICE",
            selector: (row) => row.invoice_number,
        },
        {
            id: "client",
            name: "CLIENT",
            selector: (row) => row.client_name,
        },
        {
            id: "status",
            name: "STATUS",
            cell: (row) => (
                <select
                    value={row.status || "Unpaid"}
                    disabled={processingInvoiceId === row.id}
                    onChange={(event) => updateInvoiceStatus(row, event.target.value)}
                    className={`border rounded-full px-3 py-1 text-xs font-semibold ${statusClass(row.status)}`}
                >
                    <option value="Unpaid">Unpaid</option>
                    <option value="Paid">Paid</option>
                </select>
            ),
        },
        {
            id: "loads",
            name: "LOADS",
            selector: (row) => row.loads?.length || 0,
        },
        {
            id: "invoice_date",
            name: "DATE",
            selector: (row) => row.invoice_date,
        },
        {
            id: "due_date",
            name: "DUE DATE",
            selector: (row) => row.due_date || "—",
        },
        {
            id: "total",
            name: "TOTAL",
            selector: (row) => currencyUSD(row.total),
        },
        {
            id: "action",
            name: "ACTION",
            cell: (row) => (
                <button
                    type="button"
                    onClick={() => navigate(`/account-settlement/${row.id}`)}
                    className="inline-flex items-center gap-1 text-xs text-cs-primary hover:text-cs-primary-dark"
                >
                    <span className="material-symbols-outlined text-base">visibility</span>
                    View
                </button>
            ),
        },
    ], [navigate, processingInvoiceId, updateInvoiceStatus]);

    return (
        <div className="min-h-screen bg-cs-bg-alt py-8 px-4">
            <div className="max-full mx-auto space-y-5">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                        <p className="text-[10px] uppercase tracking-[0.18em] text-cs-muted">Accounting center</p>
                        <h1 className="text-2xl font-semibold text-cs-dark">Client Invoices</h1>
                        <p className="text-sm text-cs-muted mt-1">
                            Create invoices from finished client loads and keep accounting status as Paid or Unpaid.
                        </p>
                    </div>
                    <div className="text-[10px] text-cs-muted md:text-right">
                        Total invoices: <span className="font-semibold text-cs-primary-dark">{meta.total || 0}</span>
                    </div>
                </div>

                <div className="bg-cs-bg border border-cs-border rounded-2xl shadow-sm p-4">
                    <div className="flex items-center justify-between gap-3 mb-3">
                        <div>
                            <h2 className="text-sm font-semibold text-cs-dark">Clients ready to invoice</h2>
                            <p className="text-xs text-cs-muted">Only finished/completed loads without an invoice are shown here.</p>
                        </div>
                    </div>

                    {pendingClients.length ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                            {pendingClients.map((client) => (
                                <div key={client.id} className="border border-cs-border rounded-xl p-4 bg-cs-bg-alt/40">
                                    <div className="flex items-start justify-between gap-3">
                                        <div>
                                            <h3 className="text-sm font-semibold text-cs-dark">{client.company_name}</h3>
                                            <p className="text-[11px] text-cs-muted">{client.uninvoiced_loads_count} loads ready</p>
                                        </div>
                                        <span className="text-sm font-semibold text-cs-primary-dark">
                                            {currencyUSD(client.uninvoiced_amount)}
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        disabled={processingClientId === client.id}
                                        onClick={() => createInvoiceForClient(client.id)}
                                        className="mt-4 w-full rounded-lg bg-cs-primary text-white text-xs font-medium py-2 hover:bg-cs-primary-dark disabled:opacity-60"
                                    >
                                        {processingClientId === client.id ? "Creating..." : "Create invoice"}
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-xs text-cs-muted border border-dashed border-cs-border rounded-xl p-5 text-center">
                            No clients with uninvoiced finished loads.
                        </div>
                    )}
                </div>

                <FiltersPagination
                    showFilters={true}
                    filters={filters}
                    setFilters={setFilters}
                    meta={meta}
                    statusModel="Invoice"
                >
                    <div className="bg-cs-bg border border-cs-border rounded-2xl shadow-sm overflow-hidden">
                        <DataTableElement columns={columns} items={invoices} tableLoad={tableLoad} />
                    </div>
                </FiltersPagination>
            </div>
        </div>
    );
};

export default AccountingSettlements;
